import React, { useContext, useEffect, useState } from 'react';
import Page from 'src/components/Page';

import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useNavigate } from 'react-router-dom';
import {Badge, Card, Chip, Container, Grid, Link, Stack, Typography, styled, TextField, ToggleButton, ToggleButtonGroup, Select, MenuItem, Switch} from '@mui/material';
import CustomSpinner from "./CustomSpinner";
import { getConversation, getConversationUnread } from '../service/conversationApi';
import { getUnreadMessages } from 'src/utils/messageUtils';
import { ProductImgStyle } from 'src/service/componentService';
import AuthContext from 'src/contexts/AuthContext';
import Iconify from 'src/components/Iconify';
import { fDateTime } from 'src/utils/formatTime';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Page>
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
          {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    </Page>
  );
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};

export default function CustomPaginationActionsTable() {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { customerId } = useContext(AuthContext);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('pending');
    const [sortOrder, setSortOrder] = useState('oldest');
    const [currentConvs, setCurrentConvs] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showEnvoye, setShowEnvoye] = useState(false);
    const [showAExpedier, setShowAExpedier] = useState(false);
    const [showRestitue, setShowRestitue] = useState(false);
    const navigate = useNavigate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Récupérer toutes les conversations en une seule requête
    useEffect(() => {
        const fetchConversations = async () => {
            if (!customerId) return;
            setIsLoading(true);
            try {
                const params = `?annonce.customer=${customerId}`;
                const response = await getConversation(params);
                const conversations = response['hydra:member'] || response;
                // Ne plus filtrer automatiquement, laisser les switches gérer l'affichage
                setRows(conversations);
                let notResponded = conversations.filter((conv) => conv.isPending === true);
                setCurrentConvs(notResponded);
            } catch (error) {
                console.error('Erreur lors du chargement des conversations:', error);
            } finally {
                setIsLoading(false);
            }
        };
        setRows([]);
        fetchConversations();
    }, [customerId]);

    const getNbrUnreadMessages = (messages) => {
        const unreadMessages = getUnreadMessages(messages);
        return unreadMessages.length;
    };

    // Fonction pour obtenir la couleur du statut
    const getStatusColor = (status) => {
        switch (status) {
            case 'TROUVÉ':
                return { bg: '#10B981', text: 'white' };
            case 'PERDU':
                return { bg: '#F59E0B', text: 'white' };
            case 'RESTITUÉ':
                return { bg: '#3B82F6', text: 'white' };
            case 'ENVOYÉ':
                return { bg: '#8B5CF6', text: 'white' };
            case 'A_EXPEDIER':
                return { bg: '#F97316', text: 'white' };
            case 'PAYÉ':
                return { bg: '#059669', text: 'white' };
            default:
                return { bg: '#6B7280', text: 'white' };
        }
    };

    const renderConversationItem = (conversation) => {
        const unreadCount = getNbrUnreadMessages(conversation.messages);
    
        
        // Récupérer l'image de l'annonce
        const getImageUrl = (annonce) => {
            if (!annonce) return '';
            const baseUrl = process.env.REACT_APP_PUBLIC_URL;
            if (annonce.filePath === null) {
                return `${baseUrl}/images/annonces/default/${annonce.category?.defaultImage}`;
            }
            return `${baseUrl}/images/annonces/${annonce.filePath}`;
        };

        // Récupérer l'email de l'auteur
        const authorEmail = conversation.author?.email || 'Email non disponible';
        
        // Dernier message
        const lastMessage = conversation.messages && conversation.messages.length > 0
            ? conversation.messages[conversation.messages.length - 1]
            : null;
        const isAuthor = lastMessage && lastMessage.author && lastMessage.author.id === customerId;
        let chipLabel = 'Festivalier';
        if (isAuthor) {
            chipLabel = 'Vous';
        } else if (lastMessage && lastMessage.author) {
            // Si ce n'est pas le festivalier connecté
            if (lastMessage.author.name) {
                chipLabel = lastMessage.author.name;
            } else {
                chipLabel = 'Festivalier';
            }
        }
        
        // Dans renderConversationItem, calculer si la conversation est en attente de réponse :
        // Une conversation est "en attente de réponse" si :
        // 1. Il y a des messages
        // 2. Aucun message non lu
        // 3. Le dernier message est de l'autre personne (pas du customer connecté)
        const isPendingReply = (
            conversation.messages && conversation.messages.length > 0 &&
            getNbrUnreadMessages(conversation.messages) === 0 &&
            conversation.messages[conversation.messages.length - 1].author &&
            conversation.messages[conversation.messages.length - 1].author.id !== customerId
        );
        
        return (
            <Card 
                key={conversation.id} 
                sx={{ 
                    mb: 2, 
                    p: 2,
                    backgroundColor: unreadCount > 0 ? 'action.hover' : 'background.paper',
                    '&:hover': {
                        backgroundColor: 'action.selected',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    cursor: 'pointer'
                }}
                onClick={() => handleGoToAds(conversation.annonce.id, conversation.id)}
            >
                <Stack direction="row" alignItems="center" spacing={3}>
                    {/* Vignette de l'objet */}
                    <Box sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: 2, 
                        overflow: 'hidden',
                        flexShrink: 0,
                        border: '2px solid',
                        borderColor: unreadCount > 0 ? 'error.main' : 'divider'
                    }}>
                        <img
                            src={getImageUrl(conversation.annonce)}
                            alt={conversation.annonce.title}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>

                    {/* Informations principales */}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ 
                                fontWeight: unreadCount > 0 ? 'bold' : 'normal',
                                color: unreadCount > 0 ? 'error.main' : 'text.primary'
                            }}>
                            {conversation.annonce.title}
                        </Typography>
                            {/* Chip de statut de l'annonce */}
                            <Chip 
                                label={conversation.annonce.status || 'N/A'}
                                size="small"
                                sx={{
                                    backgroundColor: getStatusColor(conversation.annonce.status || 'N/A').bg,
                                    color: getStatusColor(conversation.annonce.status || 'N/A').text,
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem'
                                }}
                            />
                            {unreadCount > 0 && (
                                <Chip 
                                    label={`${unreadCount} non lu${unreadCount > 1 ? 's' : ''}`}
                                    size="small"
                                    color="error"
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            )}

                        </Stack>

                        {/* Catégorie et email */}
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Chip 
                                label={conversation.annonce.category?.name || 'Sans catégorie'}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                fontSize: '0.875rem'
                            }}>
                                <Iconify icon="eva:email-fill" width={16} height={16} sx={{ mr: 0.5 }} />
                                {authorEmail}
                            </Typography>
                        </Stack>

                        {/* Dernier message avec extrait et auteur */}
                        {lastMessage && (
                            <Box sx={{ mt: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                                        fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                                        <Iconify icon="eva:clock-outline" width={14} height={14} sx={{ mr: 0.5 }} />
                                        {fDateTime(lastMessage.createdAt)}
                                    </Typography>
                                    {/* Indication de l'auteur du dernier message */}
                                    {lastMessage.author && (
                                        <Chip 
                                            label={chipLabel}
                                            size="small"
                                            color={isAuthor ? 'primary' : 'secondary'}
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: '20px' }}
                                        />
                                    )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ 
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%'
                                }}>
                                    {lastMessage.content || 'Aucun message'}
                        </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Badge de messages non lus */}
                    {unreadCount > 0 && (
                        <Badge badgeContent={unreadCount} color="error" sx={{ flexShrink: 0 }}>
                            <Iconify icon="eva:message-circle-fill" width={24} height={24} />
                        </Badge>
                    )}
                </Stack>
            </Card>
        );
    };

    const handleGoToAds = (annonceId, conversationId) => {
        navigate(`/dashboard/products/${annonceId}?conversation=${conversationId}&tab=chat`);
    };

    // Fonction pour obtenir la date du dernier message
    const getLastMessageDate = (conversation) => {
        if (!conversation.messages || conversation.messages.length === 0) {
            return new Date(0); // Date très ancienne si pas de messages
        }
        return new Date(conversation.messages[conversation.messages.length - 1]?.createdAt);
    };

    // Fonction utilitaire pour formater la durée depuis une date
    function formatDurationSince(dateString) {
      if (!dateString) return '';
      const now = new Date();
      const then = new Date(dateString);
      let diff = Math.floor((now - then) / 1000); // en secondes

      const days = Math.floor(diff / (3600 * 24));
      diff -= days * 3600 * 24;
      const hours = Math.floor(diff / 3600);
      diff -= hours * 3600;
      const minutes = Math.floor(diff / 60);

      let result = '';
      if (days > 0) result += `${days}j `;
      if (hours > 0 || days > 0) result += `${hours}h `;
      result += `${minutes}min`;
      return result.trim();
    }

    // Extraire dynamiquement la liste des catégories présentes dans les conversations
    const categories = Array.from(new Set(rows.map(row => row.annonce.category?.name).filter(Boolean)));

    // Filtrer et trier les conversations (après concaténation)
    const filteredAndSortedRows = rows
        .filter(conv => {
            // Gestion des switches pour les statuts
            if (!showEnvoye && conv.annonce.status === "ENVOYÉ") return false;
            if (!showAExpedier && conv.annonce.status === "A_EXPEDIER") return false;
            if (!showRestitue && conv.annonce.status === "RESTITUÉ") return false;
            
            if (!conv.messages || conv.messages.length === 0) return false;
            // Exclure si tous les messages sont vides
            if (!conv.messages.some(msg => msg.content && msg.content.trim() !== "")) return false;
            if (filterType === 'unread') {
                const unreadCount = getNbrUnreadMessages(conv.messages);
                if (unreadCount === 0) return false;
            }
            if (filterType === 'pending') {
                // Exclure les conversations sans message
                if (!conv.messages || conv.messages.length === 0) return false;
                // Exclure les conversations avec des messages non lus
                if (getNbrUnreadMessages(conv.messages) > 0) return false;
                const lastMessage = conv.messages[conv.messages.length - 1];
                // Le dernier message doit être de l'autre personne (pas du customer connecté)
                if (!(lastMessage.author && lastMessage.author.id !== customerId)) {
                    return false;
                }
            }
            if (selectedCategory && conv.annonce.category?.name !== selectedCategory) {
                return false;
            }
            if (search) {
                const lower = search.toLowerCase();
                if (conv.annonce.title && conv.annonce.title.toLowerCase().includes(lower)) return true;
                if (conv.author?.email && conv.author.email.toLowerCase().includes(lower)) return true;
                if (conv.messages && conv.messages.some(msg => msg.content && msg.content.toLowerCase().includes(lower))) return true;
                return false;
            }
            return true;
        })
        .sort((a, b) => {
            const dateA = getLastMessageDate(a);
            const dateB = getLastMessageDate(b);
            if (sortOrder === 'newest') {
                return dateB - dateA;
            } else {
                return dateA - dateB;
            }
        });

    // Fonction utilitaire pour filtrer selon le type (unread, pending, all) en tenant compte de la recherche et de la catégorie
    function getFilteredRowsByType(type) {
      return rows
        .filter(conv => {
                      // Gestion des switches pour les statuts
            if (!showEnvoye && conv.annonce.status === "ENVOYÉ") return false;
            if (!showAExpedier && conv.annonce.status === "A_EXPEDIER") return false;
            if (!showRestitue && conv.annonce.status === "RESTITUÉ") return false;
          
          if (!conv.messages || conv.messages.length === 0) return false;
          // Exclure si tous les messages sont vides
          if (!conv.messages.some(msg => msg.content && msg.content.trim() !== "")) return false;
          if (type === 'unread') {
            const unreadCount = getNbrUnreadMessages(conv.messages);
            if (unreadCount === 0) return false;
          }
          if (type === 'pending') {
        if (!conv.messages || conv.messages.length === 0) return false;
            if (getNbrUnreadMessages(conv.messages) > 0) return false;
        const lastMessage = conv.messages[conv.messages.length - 1];
            if (!(lastMessage.author && lastMessage.author.id !== customerId)) {
              return false;
            }
          }
          if (selectedCategory && conv.annonce.category?.name !== selectedCategory) {
            return false;
          }
          if (search) {
            const lower = search.toLowerCase();
            if (conv.annonce.title && conv.annonce.title.toLowerCase().includes(lower)) return true;
            if (conv.author?.email && conv.author.email.toLowerCase().includes(lower)) return true;
            if (conv.messages && conv.messages.some(msg => msg.content && msg.content.toLowerCase().includes(lower))) return true;
            return false;
          }
          return true;
        });
    }

    // Calculer dynamiquement les nombres pour chaque filtre (badges)
    const unreadCount = getFilteredRowsByType('unread').length;
    const pendingCount = getFilteredRowsByType('pending').length;
    const allCount = getFilteredRowsByType('all').length;

    // Après le calcul de filteredAndSortedRows :
  
    return (
        <Page title="Messages">
            <Container maxWidth={false}>
                <Typography variant="h4" sx={{ mb: 3 }}>
                    Messagerie
                </Typography>
                
                {/* Filtres et tri */}
                <Box sx={{ mb: 3 }}>
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 1 : 2} alignItems={isMobile ? 'stretch' : 'center'} sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ minWidth: 'fit-content' }}>
                            Filtrer :
                        </Typography>
                        <ToggleButtonGroup
                            value={filterType}
                            exclusive
                            onChange={(event, newFilterType) => {
                                if (newFilterType !== null) {
                                    setFilterType(newFilterType);
                                }
                            }}
                            size="small"
                        >
                            <ToggleButton value="pending" sx={{ px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                En attente de réponse
                                <span style={{
                                    background: '#FFA726', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: '0.85em', marginLeft: 6, minWidth: 22, textAlign: 'center', display: 'inline-block'
                                }}>{pendingCount}</span>
                            </ToggleButton>
                            <ToggleButton value="unread" sx={{ px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                Non lus
                                <span style={{
                                    background: '#d32f2f', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: '0.85em', marginLeft: 6, minWidth: 22, textAlign: 'center', display: 'inline-block'
                                }}>{unreadCount}</span>
                            </ToggleButton>
                            <ToggleButton value="all" sx={{ px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                Tous
                                <span style={{
                                    background: '#1976d2', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: '0.85em', marginLeft: 6, minWidth: 22, textAlign: 'center', display: 'inline-block'
                                }}>{allCount}</span>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="subtitle1" sx={{ minWidth: 'fit-content', ml: isMobile ? 0 : 3, mt: isMobile ? 1 : 0 }}>
                            Catégorie :
                        </Typography>
                        <Select
                            value={selectedCategory}
                            onChange={e => setSelectedCategory(e.target.value)}
                            displayEmpty
                            size="small"
                            sx={{ minWidth: 140 }}
                        >
                            <MenuItem value="">Toutes</MenuItem>
                            {categories.map(cat => (
                                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                        </Select>
                        <Typography variant="subtitle1" sx={{ minWidth: 'fit-content', ml: isMobile ? 0 : 3, mt: isMobile ? 1 : 0 }}>
                            Trier par :
                        </Typography>
                        <ToggleButtonGroup
                            value={sortOrder}
                            exclusive
                            onChange={(event, newSortOrder) => {
                                if (newSortOrder !== null) {
                                    setSortOrder(newSortOrder);
                                }
                            }}
                            size="small"
                        >
                            <ToggleButton value="newest" sx={{ px: 2 }}>
                                Plus récent
                            </ToggleButton>
                            <ToggleButton value="oldest" sx={{ px: 2 }}>
                                Plus ancien
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                    <TextField
                        label="Rechercher dans les messages..."
                        variant="outlined"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mt: isMobile ? 1 : 0 }}
                    />
                    
                    {/* Switches pour les statuts - TEMPORAIREMENT MASQUÉ */}
                    {/* 
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Afficher les conversations avec les statuts :
                        </Typography>
                        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Switch
                                    checked={showEnvoye}
                                    onChange={(e) => setShowEnvoye(e.target.checked)}
                                    size="small"
                                />
                                <Typography variant="body2" sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    color: showEnvoye ? '#8B5CF6' : 'text.secondary'
                                }}>
                                    <Box sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        backgroundColor: '#8B5CF6',
                                        mr: 0.5
                                    }} />
                                    ENVOYÉ
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Switch
                                    checked={showAExpedier}
                                    onChange={(e) => setShowAExpedier(e.target.checked)}
                                    size="small"
                                />
                                <Typography variant="body2" sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    color: showAExpedier ? '#F97316' : 'text.secondary'
                                }}>
                                    <Box sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        backgroundColor: '#F97316',
                                        mr: 0.5
                                    }} />
                                    A_EXPEDIER
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Switch
                                    checked={showRestitue}
                                    onChange={(e) => setShowRestitue(e.target.checked)}
                                    size="small"
                                />
                                <Typography variant="body2" sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    color: showRestitue ? '#3B82F6' : 'text.secondary'
                                }}>
                                    <Box sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        backgroundColor: '#3B82F6',
                                        mr: 0.5
                                    }} />
                                    RESTITUÉ
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    */}
                </Box>
                    <Box>
                    {filteredAndSortedRows.length === 0 && !isLoading ? (
                            <Typography variant="body1">
                                {filterType === 'unread' ? 'Aucune conversation avec des messages non lus' : 'Aucune conversation'}
                            </Typography>
                        ) : (
                        filteredAndSortedRows.map((conversation) =>
                            <div key={conversation.id}>
                                {/* Carte conversation adaptée mobile/desktop */}
                                <Card
                                    sx={{
                                        mb: 2,
                                        p: isMobile ? 1 : 2,
                                        backgroundColor: getNbrUnreadMessages(conversation.messages) > 0 ? 'action.hover' : 'background.paper',
                                        '&:hover': {
                                            backgroundColor: 'action.selected',
                                            transform: isMobile ? 'none' : 'translateY(-2px)',
                                            boxShadow: isMobile ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleGoToAds(conversation.annonce.id, conversation.id)}
                                >
                                    <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'stretch' : 'center'} spacing={isMobile ? 1 : 3}>
                                        {/* Vignette de l'objet */}
                                        <Box sx={{
                                            width: isMobile ? '100%' : 80,
                                            height: isMobile ? 120 : 80,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            flexShrink: 0,
                                            border: '2px solid',
                                            borderColor: getNbrUnreadMessages(conversation.messages) > 0 ? 'error.main' : 'divider',
                                            mx: isMobile ? 'auto' : 0,
                                        }}>
                                            <img
                                                src={conversation.annonce.filePath === null
                                                    ? `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/default/${conversation.annonce.category?.defaultImage}`
                                                    : `${process.env.REACT_APP_PUBLIC_URL}/images/annonces/${conversation.annonce.filePath}`}
                                                alt={conversation.annonce.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                        {/* Infos principales */}
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} spacing={1} sx={{ mb: 1 }}>
                                                <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{
                                                    fontWeight: getNbrUnreadMessages(conversation.messages) > 0 ? 'bold' : 'normal',
                                                    color: getNbrUnreadMessages(conversation.messages) > 0 ? 'error.main' : 'text.primary',
                                                    fontSize: isMobile ? '1rem' : undefined,
                                                }}>
                                                    {conversation.annonce.title}
                                                </Typography>
                                                {getNbrUnreadMessages(conversation.messages) > 0 && (
                                                    <Chip
                                                        label={`${getNbrUnreadMessages(conversation.messages)} non lu${getNbrUnreadMessages(conversation.messages) > 1 ? 's' : ''}`}
                                                        size="small"
                                                        color="error"
                                                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', height: isMobile ? 20 : undefined }}
                                                    />
                                                )}
                                                {/* Chip de statut de l'annonce */}
                                                <Chip
                                                    label={conversation.annonce.status || 'N/A'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getStatusColor(conversation.annonce.status || 'N/A').bg,
                                                        color: getStatusColor(conversation.annonce.status || 'N/A').text,
                                                        fontWeight: 'bold',
                                                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                        height: isMobile ? 20 : undefined
                                                    }}
                                                />
                                                {(() => {
                                                    const lastMessage = conversation.messages && conversation.messages.length > 0
                                                        ? conversation.messages[conversation.messages.length - 1]
                                                        : null;
                                                    const isAuthor = lastMessage && lastMessage.author && lastMessage.author.id === customerId;
                                                    let chipLabel = 'Festivalier';
                                                    if (isAuthor) {
                                                        chipLabel = 'Vous';
                                                    } else if (lastMessage && lastMessage.author) {
                                                        if (lastMessage.author.name) {
                                                            chipLabel = lastMessage.author.name;
                                                        } else {
                                                            chipLabel = 'Festivalier';
                                                        }
                                                    }
                                                    const isPendingReply = (
                                                        conversation.messages && conversation.messages.length > 0 &&
                                                        getNbrUnreadMessages(conversation.messages) === 0 &&
                                                        conversation.messages[conversation.messages.length - 1].author &&
                                                        conversation.messages[conversation.messages.length - 1].author.id !== customerId
                                                    );
                                                    return isPendingReply && filterType !== "all" && (
                                                        <Chip
                                                            label="En attente de réponse"
                                                            size="small"
                                                            sx={{ background: '#FFA726', color: '#fff', fontWeight: 600, fontSize: isMobile ? '0.7rem' : '0.75rem', height: isMobile ? 20 : undefined }}
                                                        />
                                                    );
                                                })()}
                                            </Stack>
                                            {/* Catégorie et email */}
                                            <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} spacing={2} sx={{ mb: 1 }}>
                                                <Chip
                                                    label={conversation.annonce.category?.name || 'Sans catégorie'}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem', height: isMobile ? 20 : undefined }}
                                                />
                                                <Typography variant="body2" color="text.secondary" sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                }}>
                                                    <Iconify icon="eva:email-fill" width={16} height={16} sx={{ mr: 0.5 }} />
                                                    {conversation.author?.email || 'Email non disponible'}
                                                </Typography>
                                            </Stack>
                                            {/* Dernier message avec extrait et auteur */}
                                            {(() => {
                                                const lastMessage = conversation.messages && conversation.messages.length > 0
                                                    ? conversation.messages[conversation.messages.length - 1]
                                                    : null;
                                                const isAuthor = lastMessage && lastMessage.author && lastMessage.author.id === customerId;
                                                let chipLabel = 'Festivalier';
                                                if (isAuthor) {
                                                    chipLabel = 'Vous';
                                                } else if (lastMessage && lastMessage.author) {
                                                    if (lastMessage.author.name) {
                                                        chipLabel = lastMessage.author.name;
                                                    } else {
                                                        chipLabel = 'Festivalier';
                                                    }
                                                }
                                                return lastMessage && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{
                                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}>
                                                                <Iconify icon="eva:clock-outline" width={14} height={14} sx={{ mr: 0.5 }} />
                                                                {fDateTime(lastMessage.createdAt)}
                                                            </Typography>
                                                            {lastMessage.author && (
                                                                <Chip
                                                                    label={chipLabel}
                                                                    size="small"
                                                                    color={isAuthor ? 'primary' : 'secondary'}
                                                                    variant="outlined"
                                                                    sx={{ fontSize: isMobile ? '0.7rem' : '0.7rem', height: isMobile ? 20 : '20px' }}
                                                                />
                                                            )}
                                                        </Stack>
                                                        {lastMessage && lastMessage.content && (
                                                            <Typography variant="body2" color="text.secondary" sx={{
                                                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                                fontStyle: 'italic',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                maxWidth: '100%',
                                                            }}>
                                                                {lastMessage.content}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </Box>
                                        {/* Badge de messages non lus */}
                                        {getNbrUnreadMessages(conversation.messages) > 0 && (
                                            <Badge
                                                badgeContent={getNbrUnreadMessages(conversation.messages)}
                                                color="error"
                                                sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
                                            >
                                                <Iconify icon="eva:message-circle-fill" width={24} height={24} />
                                            </Badge>
                                        )}
                                        {/* Chrono dernier message festivalier */}
                                        {(() => {
                                            // Chercher le dernier message du festivalier (auteur de la conversation)
                                            const lastFestivalierMessage = conversation.messages && conversation.author
                                                ? [...conversation.messages].reverse().find(msg => msg.author && msg.author.id === conversation.author.id)
                                                : null;
                                            return lastFestivalierMessage && filterType !== "all" ? (
                                                <Box sx={{ ml: 2, minWidth: 120, textAlign: 'right', alignSelf: 'center' }}>
                                                    <Chip
                                                        label={`Attente de : ${formatDurationSince(lastFestivalierMessage.createdAt)}`}
                                                        color="warning"
                                                        sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.8rem' }, px: 1.5, py: 0.5 }}
                                                    />
                                                </Box>
                                            ) : null;
                                        })()}
                                    </Stack>
                                </Card>
                            </div>
                        )
                    )}
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CustomSpinner />
                    </Box>
                )}
                </Box>
            </Container>
        </Page>
    );
}